import { Injectable, inject } from '@angular/core';
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr';

import { TelegramErrorReporterService } from 'src/app/core/services/telegram-error-reporter.service';

/**
 * `ToastrService` with error toasts mirrored to the Telegram alert channel.
 *
 * Substituted for `ToastrService` in the root injector (app.module), so every existing
 * `this.toastr.error(...)` call site — including HttpErrorInterceptorService and
 * `ErrorHandlerDirective.handleError` — is covered without being touched, and any new one is
 * covered automatically.
 *
 * Only `error()` is mirrored. `warning()` is mostly guidance, and success/info never
 * indicate a fault.
 *
 * The toast is what the user actually saw, which is why it is worth reporting even though
 * many error toasts follow an API failure the interceptor already reported: the interceptor
 * knows the request failed, this knows whether the user was told, and in what words. The
 * reporter's echo window collapses the pair when both carry the same text.
 */
@Injectable()
export class TrackedToastrService extends ToastrService {
  private readonly reporter = inject(TelegramErrorReporterService);

  /**
   * Reports the message, then shows the error toast exactly as ToastrService would.
   * @param {string} [message] toast body
   * @param {string} [title] toast title
   * @param {Partial<IndividualConfig<ConfigPayload>>} [override] per-toast config
   * @returns {ActiveToast<unknown>} the toast that was shown
   */
  override error<ConfigPayload = unknown>(
    message?: string,
    title?: string,
    override?: Partial<IndividualConfig<ConfigPayload>>
  ): ActiveToast<unknown> {
    this.reportToast(message, title);
    return super.error(message, title, override);
  }

  private reportToast(message?: string, title?: string): void {
    const text = String(message ?? '').trim();
    if (!text) {
      return;
    }

    this.reporter.report({
      type: 'USER_MESSAGE',
      message: text,
      name: title ? String(title).trim() : undefined
    });
  }
}
